import { ArrowLeftOutlined, CheckCircleOutlined, RollbackOutlined, StopOutlined, SwapOutlined } from '@ant-design/icons';
import type { ReturnCheckRequest, ReturnItemRow, ReturnPickupRequest } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hq } from '../api/client';
import { ellipsisCell, StickyActionBar, sumWidth } from '@hq/ui-kit';

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待提交', color: 'default' },
  SUBMITTED: { text: '已提交', color: 'blue' },
  AUDITED: { text: '已审核', color: 'cyan' },
  PICKING: { text: '取件中', color: 'processing' },
  RECEIVED: { text: '已收回', color: 'blue' },
  CHECKED: { text: '已判定', color: 'cyan' },
  REFUNDED: { text: '已退款', color: 'green' },
  EXCHANGED: { text: '已换货', color: 'green' },
  CLOSED: { text: '已关闭', color: 'default' },
  REJECTED: { text: '不予受理', color: 'red' }
};

const TYPE_TEXT: Record<string, string> = {
  RETURN_REFUND: '退货退款',
  EXCHANGE: '换货',
  DISCOUNT: '折让'
};

const REASON_TEXT: Record<string, string> = {
  QUALITY: '质量问题',
  WRONG_ITEM: '错发/漏发',
  NEAR_EXPIRY: '近效期',
  DAMAGE: '破损',
  OVER_ORDER: '超量配送',
  POLICY: '政策调整'
};

const LINE_STATUS_TEXT: Record<string, string> = {
  NEW: '待收回',
  PICKED: '已取件',
  RECEIVED: '已回仓',
  CLOSED: '已关闭'
};

const PICKUP_ALLOWED = new Set(['NEW', 'SUBMITTED', 'AUDITED']);
// 责任判定（退款/换货）必须建立在实物回仓之后：后端 `ReturnOrder.CheckAsync` 强制
// `return_status == 'RECEIVED'`，否则会出现「已红冲但库存未回补」。与 canExchange 口径一致。
const CHECK_ALLOWED = new Set(['RECEIVED']);
const REJECT_ALLOWED = new Set(['NEW', 'SUBMITTED', 'AUDITED', 'PICKING']);
const CLOSE_ALLOWED = new Set(['REFUNDED', 'EXCHANGED']);
const DONE_STATUSES = new Set(['RECEIVED', 'CHECKED', 'REFUNDED', 'EXCHANGED', 'CLOSED', 'REJECTED']);

interface DeliveryDraft {
  deliverUserId: string;
  deliverName: string;
  vehicleNo: string;
  planTime: string;
}

const EMPTY_DELIVERY: DeliveryDraft = { deliverUserId: '', deliverName: '', vehicleNo: '', planTime: '' };

export default function ReturnDetail() {
  const { id } = useParams<{ id: string }>();
  const returnId = id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const [pickupOpen, setPickupOpen] = useState(false);
  const [pickupDraft, setPickupDraft] = useState<DeliveryDraft>(EMPTY_DELIVERY);

  const [checkOpen, setCheckOpen] = useState(false);
  const [checkResult, setCheckResult] = useState<'REFUND' | 'EXCHANGE'>('REFUND');
  const [handleResult, setHandleResult] = useState('');
  const [checkDraft, setCheckDraft] = useState<DeliveryDraft>(EMPTY_DELIVERY);

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  // 回仓实收：按行录入，少收/破损必须在此调整，否则后端按应收数量全额入库。
  const [receivedDraft, setReceivedDraft] = useState<Record<string, number>>({});

  const query = useQuery({
    queryKey: ['return', 'detail', returnId],
    placeholderData: keepPreviousData,
    queryFn: () => hq.returns.detail(returnId),
    enabled: Boolean(returnId)
  });

  // 实收默认值：优先用单据已有实收，其次按应收；只在缺键时补，避免覆盖用户已改的值
  useEffect(() => {
    if (!query.data) return;
    setReceivedDraft((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const item of query.data.items) {
        if (next[item.id] === undefined) {
          next[item.id] = item.receivedQty > 0 ? item.receivedQty : item.returnQty;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [query.data]);

  const finish = async (text: string) => {
    message.success(text);
    await queryClient.invalidateQueries({ queryKey: ['return'] });
    await query.refetch();
  };

  const pickupMutation = useMutation({
    mutationFn: (body: ReturnPickupRequest) => hq.returns.pickup(returnId, body),
    onSuccess: async (result) => {
      setPickupOpen(false);
      setPickupDraft(EMPTY_DELIVERY);
      await finish(`取件单已生成：${result.pickupDeliveryId}`);
    }
  });

  const confirmMutation = useMutation({
    mutationFn: () => {
      // 带上按行实收：差异（少收/破损）必须显式提交，否则后端按应收数量全额入库
      const items = (query.data?.items ?? []).map((item) => ({
        itemId: item.id,
        receivedQty: receivedDraft[item.id] ?? (item.receivedQty > 0 ? item.receivedQty : item.returnQty)
      }));
      return hq.returns.confirm(returnId, items.length > 0 ? { items } : undefined);
    },
    onSuccess: async (result) => finish(`已回仓 ${formatQty(result.totalQty)}，状态置为已收回`)
  });

  const checkMutation = useMutation({
    mutationFn: (body: ReturnCheckRequest) => hq.returns.check(returnId, body),
    onSuccess: async (result) => {
      setCheckOpen(false);
      setHandleResult('');
      setCheckDraft(EMPTY_DELIVERY);
      await finish(
        result.status === 'EXCHANGED'
          ? '已判定换货并生成补发配送单'
          : `已判定退款，红冲已并入对账单 ${result.settleBizNo ?? '-'}`
      );
    }
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => hq.returns.reject(returnId, { reason: reason || undefined }),
    onSuccess: async () => {
      setRejectOpen(false);
      setRejectReason('');
      await finish('已驳回该退货单');
    }
  });

  const closeMutation = useMutation({
    mutationFn: () => hq.returns.close(returnId),
    onSuccess: async () => finish('退货单已关闭')
  });

  if (query.isLoading) {
    return (
      <Card className="page-card">
        <Spin />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card className="page-card">
        <Alert
          type="error"
          showIcon
          message="退货单加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
        <Button style={{ marginTop: 16 }} onClick={() => navigate('/return')}>
          返回列表
        </Button>
      </Card>
    );
  }

  const { returnOrder: doc, items } = query.data;
  const meta = STATUS_META[doc.returnStatus] ?? { text: doc.returnStatus, color: 'default' };
  const canPickup = PICKUP_ALLOWED.has(doc.returnStatus);
  const canConfirm = !DONE_STATUSES.has(doc.returnStatus);
  const canCheck = CHECK_ALLOWED.has(doc.returnStatus);
  const canReject = REJECT_ALLOWED.has(doc.returnStatus);
  const canClose = CLOSE_ALLOWED.has(doc.returnStatus);
  const canExchange = doc.returnStatus === 'RECEIVED' && doc.returnType === 'EXCHANGE';

  const submitPickup = () => {
    pickupMutation.mutate({
      deliverUserId: pickupDraft.deliverUserId || undefined,
      deliverName: pickupDraft.deliverName || undefined,
      vehicleNo: pickupDraft.vehicleNo || undefined,
      planTime: pickupDraft.planTime || undefined
    });
  };

  const submitCheck = () => {
    checkMutation.mutate({
      result: checkResult,
      handleResult: handleResult || undefined,
      deliverUserId: checkResult === 'EXCHANGE' ? checkDraft.deliverUserId || undefined : undefined,
      deliverName: checkResult === 'EXCHANGE' ? checkDraft.deliverName || undefined : undefined,
      vehicleNo: checkResult === 'EXCHANGE' ? checkDraft.vehicleNo || undefined : undefined,
      planTime: checkResult === 'EXCHANGE' ? checkDraft.planTime || undefined : undefined
    });
  };

  const confirmReopen = () =>
    modal.confirm({
      title: '确认原货已回仓？',
      content: '将按明细中的实收数量增加退回仓库存并写 RETURN_IN 流水，单据置为「已收回」。实收小于退货数量时请先在明细中调整。',
      okText: '确认回仓',
      cancelText: '取消',
      onOk: () => confirmMutation.mutateAsync()
    });

  const finishClose = () =>
    modal.confirm({
      title: '关闭该退货单？',
      content: '关单后不可再变更，供应商扣款/对账以关闭状态为准。',
      okText: '关单',
      cancelText: '取消',
      onOk: () => closeMutation.mutateAsync()
    });

  const itemColumns: ColumnsType<ReturnItemRow> = [
    { title: '行号', dataIndex: 'lineNo', width: 70 },
    { title: '物资编码', dataIndex: 'materialCode', width: 140, render: (value: string | null) => value || '-' },
    { title: '物资名称', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
    { title: '单位', dataIndex: 'unitName', width: 90, render: (value: string | null) => value || '-' },
    { title: '批号', dataIndex: 'batchNo', width: 130, render: (value: string | null) => value || '-' },
    { title: '原购买量', dataIndex: 'buyQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '退货数量', dataIndex: 'returnQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    {
      title: '实收',
      dataIndex: 'receivedQty',
      width: 130,
      align: 'right',
      render: (_value: number, row: ReturnItemRow) => {
        // 待回仓时可编辑：少收/破损按实际收回数量录入，库存只按实收回补
        if (!canConfirm || row.lineStatus === 'RECEIVED') return formatQty(row.receivedQty);
        return (
          <InputNumber
            size="small"
            min={0}
            max={row.returnQty}
            precision={4}
            style={{ width: '100%' }}
            value={receivedDraft[row.id] ?? row.returnQty}
            onChange={(next) =>
              setReceivedDraft((prev) => ({ ...prev, [row.id]: typeof next === 'number' ? next : 0 }))
            }
            aria-label={`第${row.lineNo}行实收数量`}
          />
        );
      }
    },
    { title: '单价', dataIndex: 'price', width: 110, align: 'right', render: (value: number) => formatAmount(value, 4) },
    {
      title: '退货金额',
      dataIndex: 'returnAmount',
      width: 130,
      align: 'right',
      render: (value: number) => <Typography.Text type="danger">{formatAmount(value)}</Typography.Text>
    },
    { title: '质量问题', dataIndex: 'qualityDesc', ellipsis: { showTitle: false }, render: ellipsisCell('qualityDesc') },
    {
      title: '行状态',
      dataIndex: 'lineStatus',
      width: 100,
      render: (value: string | null) => (value ? LINE_STATUS_TEXT[value] ?? value : '-')
    }
  ];

  return (
    <>
      <Card
        className="page-card"
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/return')} />
            <span>{doc.bizNo}</span>
            <Tag color={meta.color}>{meta.text}</Tag>
          </Space>
        }
        extra={null}
      >
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="退货类型">{TYPE_TEXT[doc.returnType] ?? doc.returnType}</Descriptions.Item>
          <Descriptions.Item label="退货原因">{doc.reasonType ? REASON_TEXT[doc.reasonType] ?? doc.reasonType : '-'}</Descriptions.Item>
          <Descriptions.Item label="责任供应商">{doc.supplierName || '-'}</Descriptions.Item>
          <Descriptions.Item label="原单号">{doc.sourceBizNo || '-'}</Descriptions.Item>
          <Descriptions.Item label="总数量">{formatQty(doc.totalQty)}</Descriptions.Item>
          <Descriptions.Item label="退货金额">
            <Typography.Text type="danger">{formatAmount(doc.returnAmount)}</Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="业务时间">{formatDisplayTime(doc.bizDate)}</Descriptions.Item>
          <Descriptions.Item label="取件单">
            {doc.pickupDeliveryId ? (
              <Typography.Link onClick={() => navigate(`/delivery/${doc.pickupDeliveryId}`)}>查看配送单</Typography.Link>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="换货补发单">
            {doc.newDeliveryId ? (
              <Typography.Link onClick={() => navigate(`/delivery/${doc.newDeliveryId}`)}>查看配送单</Typography.Link>
            ) : (
              '-'
            )}
          </Descriptions.Item>
          <Descriptions.Item label="处理结果" span={3}>
            {doc.handleResult || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="备注" span={3}>
            {doc.remark || '-'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card className="page-card" title="退货明细">
        {items.length === 0 ? (
          <Empty description="无明细" />
        ) : (
          <Table<ReturnItemRow>
            rowKey="id"
            size="middle"
            columns={itemColumns}
            dataSource={items}
            pagination={false}
            scroll={{ x: sumWidth(itemColumns) }}
          />
        )}
      </Card>

      {canPickup || canConfirm || canCheck || canReject || canClose ? (
        <StickyActionBar>
          {canPickup ? (
            <Button icon={<RollbackOutlined />} onClick={() => setPickupOpen(true)}>
              {doc.pickupDeliveryId ? '重新生成取件单' : '生成取件单'}
            </Button>
          ) : null}
          {canConfirm ? (
            <Button type="primary" icon={<CheckCircleOutlined />} onClick={confirmReopen} loading={confirmMutation.isPending}>
              确认回仓
            </Button>
          ) : null}
          {canCheck ? (
            <Button icon={<SwapOutlined />} onClick={() => setCheckOpen(true)}>
              责任判定
            </Button>
          ) : null}
          {canReject ? (
            <Button danger icon={<StopOutlined />} onClick={() => setRejectOpen(true)}>
              驳回
            </Button>
          ) : null}
          {canClose ? (
            <Button onClick={finishClose} loading={closeMutation.isPending}>
              关单
            </Button>
          ) : null}
        </StickyActionBar>
      ) : null}

      <ModalShell
        title="生成退货取件单"
        open={pickupOpen}
        onCancel={() => setPickupOpen(false)}
        onOk={submitPickup}
        okText="生成取件单"
        confirmLoading={pickupMutation.isPending}
        error={pickupMutation.error}
        errorTitle="取件单生成失败"
      >
        <DeliveryFields value={pickupDraft} onChange={setPickupDraft} />
        <Typography.Text type="secondary">取件单为 RETURN_PICK 类型，单据状态将置为「取件中」。</Typography.Text>
      </ModalShell>

      <ModalShell
        title="责任判定"
        open={checkOpen}
        onCancel={() => setCheckOpen(false)}
        onOk={submitCheck}
        okText="提交判定"
        confirmLoading={checkMutation.isPending}
        error={checkMutation.error}
        errorTitle="判定失败"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Radio.Group
            value={checkResult}
            onChange={(event) => setCheckResult(event.target.value as 'REFUND' | 'EXCHANGE')}
            options={[
              { label: '退款（REFUNDED）', value: 'REFUND' },
              { label: canExchange ? '换货补发（EXCHANGED）' : '换货补发（需先确认回仓）', value: 'EXCHANGE', disabled: !canExchange }
            ]}
          />
          <Input.TextArea
            rows={2}
            maxLength={500}
            showCount
            placeholder="处理结果（写入 handle_result）"
            value={handleResult}
            onChange={(event) => setHandleResult(event.target.value)}
          />
          {checkResult === 'EXCHANGE' ? <DeliveryFields value={checkDraft} onChange={setCheckDraft} /> : null}
          <Typography.Text type="secondary">
            换货将按实收数量生成补发配送单并回写 new_delivery_id，走正常配送签收链路。
          </Typography.Text>
        </Space>
      </ModalShell>

      <ModalShell
        title="驳回退货单"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={() => rejectMutation.mutate(rejectReason)}
        okText="确认驳回"
        okDanger
        confirmLoading={rejectMutation.isPending}
        error={rejectMutation.error}
        errorTitle="驳回失败"
      >
        <Input.TextArea
          rows={2}
          maxLength={500}
          showCount
          placeholder="驳回原因（写入 handle_result）"
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
        />
      </ModalShell>
    </>
  );
}

function DeliveryFields({ value, onChange }: { value: DeliveryDraft; onChange: (next: DeliveryDraft) => void }) {
  // 配送人改人员选择器：手填 userId 既不知道是谁，写错只会指派给不存在的人（姓名也不再手填）
  const [keyword, setKeyword] = useState('');
  const staff = useQuery({
    queryKey: ['approval', 'user-search', keyword],
    staleTime: 30_000,
    queryFn: () => hq.approval.searchUsers(keyword || undefined, 50)
  });
  const staffOptions = (staff.data ?? []).map((row) => ({
    value: row.id,
    label: `${row.name ?? '未命名'}（${row.userCode ?? row.id}）`
  }));

  return (
    <Space wrap size={12} style={{ marginBottom: 12 }}>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="配送人（姓名 / 工号）"
        style={{ width: 240 }}
        loading={staff.isFetching}
        value={value.deliverUserId || undefined}
        onSearch={setKeyword}
        options={staffOptions}
        onChange={(id?: string) => {
          const picked = (staff.data ?? []).find((row) => row.id === id);
          onChange({ ...value, deliverUserId: id ?? '', deliverName: picked?.name ?? '' });
        }}
      />
      <Input
        placeholder="车辆"
        value={value.vehicleNo}
        onChange={(event) => onChange({ ...value, vehicleNo: event.target.value })}
        style={{ width: 140 }}
      />
      <DatePicker
        showTime
        placeholder="计划时间"
        style={{ width: 200 }}
        onChange={(date) => onChange({ ...value, planTime: date ? date.format('YYYY-MM-DD HH:mm:ss') : '' })}
      />
    </Space>
  );
}

interface ModalShellProps {
  title: string;
  open: boolean;
  onCancel: () => void;
  onOk: () => void;
  okText: string;
  okDanger?: boolean;
  confirmLoading: boolean;
  error: unknown;
  errorTitle: string;
  children: ReactNode;
}

function ModalShell({ title, open, onCancel, onOk, okText, okDanger, confirmLoading, error, errorTitle, children }: ModalShellProps) {
  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      onOk={onOk}
      okText={okText}
      okButtonProps={okDanger ? { danger: true } : undefined}
      confirmLoading={confirmLoading}
      width={720}
      destroyOnHidden
    >
      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={errorTitle}
          description={error instanceof ApiError ? error.friendlyMessage : String(error)}
        />
      ) : null}
      {children}
    </Modal>
  );
}
